import { useIntersectionObserver } from '@ab-core-functions/observer';
import { Availabilities } from '@ab-core/availabilities';
import { getClassNames } from '@ab-core/functions';
import { isLoggedIn } from '@ab-core/hooks';
import { Prices } from '@ab-core/prices';
import { ComparisonCheckbox } from '@ab-core/product-comparison/checkbox';
import type { FC } from 'react';
import React from 'react';
import {
    AdditionalLinksContainer,
    IconsContainer,
    IdsContainer,
    ImageContainer,
    InformationContainer,
    NameContainer,
    OrderContainer,
    PositionTextContainer,
    TagsContainer
} from '../../components';
import type { DefaultScalableProductCardProps } from '../index';
import { getScalablePropsFromDefaultProps } from '../utils';
import { HorizontalLine, ProductCardTileIdsAndIcons, SpacerHorizontalLine } from './styled';

export const ScalableProductCardTile: FC<DefaultScalableProductCardProps> = (props) => {
    const { ref, isVisible } = useIntersectionObserver<HTMLDivElement>({
        threshold: 0.5,
        freezeOnceVisible: true
    });
    const {
        topContent,
        tags,
        image,
        ids,
        icons,
        name,
        availability,
        prices,
        order,
        positionText,
        additionalLinks,
        information,
        comparison,
        bottomContent
    } = getScalablePropsFromDefaultProps({ ...props, isVisible });
    const isUserLoggedIn = isLoggedIn();
    const showPositionText = positionText.scalableStyle?.isVisible && isUserLoggedIn;
    const showPricesSection = prices.pricesStyles?.isVisible !== false;
    const showPricesLine =
        prices.pricesStyles?.showSurcharges ||
        prices.pricesStyles?.uvp.showUvpWithoutVat ||
        prices.pricesStyles?.uvp.showUvpWithVat;

    const spacerClassName = getClassNames({
        prefix: 'scalable-product-card-spacer',
        modifier: { showLine: showPricesSection }
    });

    return (
        <>
            {topContent && (
                <>
                    {topContent}
                    {name?.scalableStyle?.isVisible && <HorizontalLine />}
                </>
            )}
            <TagsContainer {...tags} />
            <ImageContainer {...image} />
            <div ref={ref}>
                <ProductCardTileIdsAndIcons>
                    <IdsContainer {...ids} />
                    <IconsContainer {...icons} />
                </ProductCardTileIdsAndIcons>
                <NameContainer {...name} />
            </div>
            {availability.availabilitiesStyles?.isVisible && (
                <>
                    <HorizontalLine />
                    <div>
                        <Availabilities {...availability} />
                    </div>
                </>
            )}
            <SpacerHorizontalLine className={spacerClassName} />
            {isUserLoggedIn && showPricesSection && (
                <>
                    <div>
                        <Prices {...prices} />
                    </div>
                    {showPricesLine && <HorizontalLine />}
                </>
            )}
            <OrderContainer {...order} />
            {showPositionText && (
                <>
                    <HorizontalLine />
                    <PositionTextContainer {...positionText} />
                </>
            )}
            <AdditionalLinksContainer {...additionalLinks} />
            {comparison?.scalableStyle?.isVisible && <ComparisonCheckbox {...comparison} />}
            <InformationContainer {...information} />
            {bottomContent && (
                <>
                    <HorizontalLine />
                    {bottomContent}
                </>
            )}
        </>
    );
};
